import { Fragment, memo } from 'react';
import tw from 'twin.macro';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import _ from 'lodash';
import clsx from 'clsx';
import Dropdown from '../../Dropdown';
import Filter from '../Filter';

import { DiscountEligibility, DiscountOptions } from '../../../constants';
import Checkbox from '../../Checkbox';

const useFormStyle = makeStyles(() => ({
  root: {
    '&': tw`flex-nowrap pl-4 w-full`,
  },
}));

const MultiCheckboxSelectFilter = ({
  options,
  values,
  onChange,
  label,
  variant,
  filterMaxWidthClass,
  children,
  sortField,
  widthInside,
  menuWidthClass,
}) => {
  const formClasses = useFormStyle();

  const getLabel = () => {
    const result = _.map(_.uniq(_.flatMapDeep(_.filter(values, (f) => !!f), (v) => (v && typeof (v[0]) === 'object' ? _.map(v, (v1) => v1.discounts) : v))), (k) => (k ? DiscountOptions[k].label : ''));
    return result.length ? result.join(', ') : label;
  };

  const handleClear = () => {
    _.each(values, (val, key) => {
      let newValues = [];
      if (key === 'staterooms') {
        newValues = [...val];
        newValues.forEach((v, index) => {
          newValues[index] = {
            ...newValues[index],
            [sortField]: [],
          };
        });
      }
      onChange(key, newValues);
    });
  };

  const handleChange = (item, newValue, index) => {
    let newValues = [];
    if (item.valueProperty === 'staterooms') {
      newValues = [...values[item.valueProperty]];
      const discounts = [...newValues[index][sortField]];
      let newDiscounts;
      if (!newValues[index].pastPassengerNumbers) {
        newValues[index] = {
          pastPassengerNumbers: [],
        };
      }

      if (newValue && !discounts.includes(item.value)) {
        discounts.push(item.value);
        if (item.value === DiscountEligibility.PAST_PASSENGER
          && !newValues[index].pastPassengerNumbers.length) {
          newValues[index] = {
            ...newValues[index],
            pastPassengerNumbers: [
              ...newValues[index].pastPassengerNumbers,
              {},
            ],
          };
        }
      } else if (!newValue) {
        newDiscounts = discounts.filter((v) => v !== item.value);
      }
      newValues[index] = {
        ...newValues[index],
        [sortField]: newDiscounts || discounts,
      };
    } else {
      newValues = [...values[item.valueProperty]];
      if (newValue && !newValues.includes(item.value)) {
        newValues.push(item.value);
      } else if (!newValue) {
        newValues = newValues.filter((v) => v !== item.value);
      }
    }
    onChange(item.valueProperty, newValues);
  };

  return (
    <Filter
      label={getLabel()}
      menuWidthClass={menuWidthClass}
      variant={variant}
      widthInside={widthInside}
      filterMaxWidthClass={filterMaxWidthClass}
      isFilterEmpty={getLabel() === label}
      handleClear={handleClear}
    >
      {options.map((opt, optionIndex) => (
        <Dropdown.Item key={`${optionIndex.toString()}`} classes={clsx(opt.length > 0 ? 'pt-0' : 'pb-1 pt-1', { 'pt-4': optionIndex === 0 })}>
          <FormControl
            className="grid col-span-5 grid-cols-2 gap-y-2 w-full items-center"
            component="fieldset"
          >
            <FormGroup classes={formClasses} className="overflow-x-auto max-h-1">
              {opt && opt.length ? opt.map((item) => (
                <Fragment key={item.value}>
                  {item.doShowStateroomLabel && item.stateroomIndex >= 0 && (
                    <div className={clsx('col-span-5 bg-blue-200 rounded px-2 py-1 text-white font-bold text-theme', {
                      'mt-4': item.stateroomIndex === 0,
                    })}
                    >
                      Stateroom
                      {' '}
                      {item.stateroomIndex + 1}
                    </div>
                  )}
                  <Checkbox
                    checked={values[item.valueProperty][item.stateroomIndex]
                      && (values[item.valueProperty][item.stateroomIndex][sortField] || [])
                        .includes(item.value)}
                    name={item.label}
                    onChange={
                      (event, newValue) => handleChange(item, newValue, item.stateroomIndex)
                    }
                    value={item.value}
                    label={item.label}
                    key={item.value}
                  />
                  {values[item.valueProperty][item.stateroomIndex]
                    && values[item.valueProperty][item.stateroomIndex][sortField]
                    && values[item.valueProperty][item.stateroomIndex][sortField].includes(item.value) && item.inputElement ? item.inputElement : ''}
                </Fragment>
              )) : (
                <>
                  <Checkbox
                    checked={(values[opt.valueProperty] || []).includes(opt.value)}
                    name={opt.label}
                    onChange={(event, newValue) => handleChange(opt, newValue, optionIndex)}
                    value={opt.value}
                    label={opt.label}
                    key={opt.value}
                  />
                  {opt.inputElement ? opt.inputElement : ''}
                </>
              )}
              {children}
            </FormGroup>
          </FormControl>
        </Dropdown.Item>
      ))}
    </Filter>
  );
};

MultiCheckboxSelectFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    inputElement: PropTypes.node,
  })), PropTypes.shape()])).isRequired,
  values: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape()),
    PropTypes.shape()])).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  sortField: PropTypes.string.isRequired,
  variant: PropTypes.string,
  widthInside: PropTypes.string,
  menuWidthClass: PropTypes.string,
  filterMaxWidthClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

MultiCheckboxSelectFilter.defaultProps = {
  variant: 'standard',
  filterMaxWidthClass: 'max-w-filter',
  children: null,
  widthInside: null,
  menuWidthClass: 'w-64',
};

export default memo(MultiCheckboxSelectFilter);
